import { z } from 'zod';
import { uuidSchema } from './common.schema';

export const PRODUCT_STATUSES = ['active', 'draft', 'deprecated', 'archived'] as const;
export type ProductStatus = (typeof PRODUCT_STATUSES)[number];

export const PRODUCT_CATEGORIES = [
  'Altered Fat',
  'Balanced Nutritional Drink',
  'Calorie And Protein Enhancer',
  'Cereal',
  'Cho Module',
  'Clear Nutritional Drink',
  'Clinical Condition Specific',
  'Concentrated Human Milk Derived Fortifier',
  'Dietary Supplement',
  'Disease Specific',
  'Donor Milk',
  'Donor Milk Derived Polymeric Formula',
  'Electrolyte Replacement',
  'Elemental',
  'Energy Module',
  'Fat & Cho Module',
  'Fat Module',
  'Flavored Nutritional Drink',
  'Food Blend',
  'Food Item',
  'High Calorie',
  'Human Milk',
  'Hydration',
  'Hypoallergenic',
  'Hypoallergenic Protein Module',
  'Immune-Enhancing',
  'Individual AA Supplement',
  'Intact',
  'Low Residue',
  'Metabolic',
  'Mineral',
  'Mineral Module',
  'Modular',
  'Nutritional Drink',
  'Nutritional Pudding',
  'Oral Hydration',
  'Poly',
  'Polymeric',
  'Protein',
  'Protein Module',
  'Protein Supplement',
  'Semi-Elemental',
  'Supplement',
  'Vitamin',
  'Wound Healing',
] as const;
export type ProductCategory = (typeof PRODUCT_CATEGORIES)[number];

export const PRODUCT_FORM_TYPES = [
  'Concentrate',
  'Donor Milk',
  'Food Blend',
  'Human Milk',
  'Powder',
  'Ready to Feed',
  'Ready to Hang',
  'Sterile Water',
] as const;
export type ProductFormType = (typeof PRODUCT_FORM_TYPES)[number];

/**
 * Case-insensitive normalizer for form_type values.
 * Maps any casing variant (e.g. 'powder', 'POWDER') to the canonical form
 * defined in PRODUCT_FORM_TYPES (e.g. 'Powder'). Returns the original value
 * unchanged if no match is found (validation will then reject it with a clear
 * enum error).
 */
const normalizeFormType = (v: unknown): unknown => {
  if (typeof v !== 'string') return v;
  const lower = v.toLowerCase();
  const match = PRODUCT_FORM_TYPES.find((t) => t.toLowerCase() === lower);
  return match ?? v;
};

export function normalizeShelfLifeUnit(value: unknown, defaultUnit: 'days' | 'hours'): 'days' | 'hours' {
  if (value == null) return defaultUnit;
  const unit = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (['d', 'day', 'days'].includes(unit)) return 'days';
  if (['h', 'hr', 'hrs', 'hour', 'hours'].includes(unit)) return 'hours';
  return defaultUnit;
}

const shelfLifeUnit = z.preprocess(
  (v) => (typeof v === 'string' ? v.trim().toLowerCase() : v),
  z.enum(['d', 'day', 'days', 'h', 'hr', 'hrs', 'hour', 'hours']).nullable().optional(),
);

// ---------------------------------------------------------------------------
// Field helpers
// ---------------------------------------------------------------------------
const decimalField = () => z.coerce.number().nullable().optional();
const _intField = () => z.coerce.number().int().nullable().optional();
const strField = (max: number) => z.string().max(max).nullable().optional();
const strArrayField = (max: number) => z.array(z.string().max(max)).nullable().optional();
const boolField = () => z.boolean().nullable().optional();
// Accepts a valid UUID, null, undefined, or empty string (empty → undefined).
const optionalUuidField = () =>
  z.preprocess(
    (v) => (v === '' || v === null ? undefined : v),
    uuidSchema.nullable().optional()
  );

// ---------------------------------------------------------------------------
// Flat product fields -- mirrors the SQL product table 1-to-1.
// Used as the base for Create, Patch, Detail and List schemas.
// ---------------------------------------------------------------------------
const productFlatFields = {
  // -- Identity ---------------------------------------------------------------
  display_name:             strField(255),
  image_url:                z.string().url().nullable().optional(),
  brand_name:               strField(255),
  form_name:                strField(64),
  category_name:            strArrayField(128),
  manufacturer_name:        strField(255),
  description:              z.string().nullable().optional(),
  type_of_product:          strField(64),
  item_type:                strField(64),
  status:                   z.enum(PRODUCT_STATUSES).nullable().optional(),
  is_active:                boolField(),

  // -- Tenant / Multi-tenancy --------------------------------------------------
  tenant_id:                z.string().nullable().optional(),
  master_product_id:        optionalUuidField(),
  is_catalog_copy:          boolField(),
  catalog_added_by:         optionalUuidField(),
  catalog_added_at:         z.string().nullable().optional(),
  is_preferred:             boolField(),
  custom_settings:          z.record(z.string(), z.unknown()).nullable().optional(),

  // -- GSI key attributes ------------------------------------------------------
  gsi1pk: strField(128), gsi1sk: strField(128),
  gsi2pk: strField(128), gsi2sk: strField(128),
  gsi3pk: strField(128), gsi3sk: strField(128),
  gsi4pk: strField(128), gsi4sk: strField(128),
  gsi5pk: strField(128), gsi5sk: strField(128),
  gsi6pk: strField(128), gsi6sk: strField(128),

  // -- Allergen flags (EU 14 + extras) -----------------------------------------
  contains_allergen_milk:       boolField(),
  contains_allergen_egg:        boolField(),
  contains_allergen_wheat:      boolField(),
  contains_allergen_soy:        boolField(),
  contains_allergen_peanut:     boolField(),
  contains_allergen_tree_nuts:  boolField(),
  contains_allergen_fish:       boolField(),
  contains_allergen_shellfish:  boolField(),
  contains_allergen_sesame:     boolField(),
  contains_allergen_celery:     boolField(),
  contains_allergen_mustard:    boolField(),
  contains_allergen_lupin:      boolField(),
  contains_allergen_molluscs:   boolField(),
  contains_allergen_sulphites:  boolField(),
  contains_allergen_lactose:    boolField(),
  contains_allergen_latex:      boolField(),
  contains_allergen_gluten:     boolField(),
  contains_allergen_crustaceans:     boolField(),
  contains_allergen_sulphur_dioxide: boolField(),

  // -- Batch / Verification ----------------------------------------------------
  csv_batch_ref:        strField(128),
  date_verified:        z.string().nullable().optional(),
  verification_source:  z.string().nullable().optional(),
  substitutions:        z.string().nullable().optional(),

  // -- Coding & Formulary keys --------------------------------------------------
  overarching_number_pens:  strField(64),
  overarching_number_ns:    strField(64),
  overarching_number_tmnp:  strField(64),
  hcpcs:                    strField(32),
  ndc_format_code:          strField(32),
  tms_product_id:           strField(64),

  // -- QA flags ----------------------------------------------------------------
  needs_review:       boolField(),
  similar_products:   z.preprocess(
    (v) => {
      if (typeof v === 'string') {
        try { return JSON.parse(v); } catch { return v; }
      }
      return v;
    },
    z.array(z.string()).nullable().optional(),
  ),
  similarity_details: z.string().nullable().optional(),

  // -- Macronutrients ----------------------------------------------------------
  calories_kcal:        decimalField(),
  total_fat_g:          decimalField(),
  saturated_fat_g:      decimalField(),
  trans_fat_g:          decimalField(),
  cholesterol_mg:       decimalField(),
  cho_g:                decimalField(),
  sodium_mg:            decimalField(),
  sodium_meq:           decimalField(),
  calories_per_ml:      decimalField(),
  total_carbohydrate_g: decimalField(),
  dietary_fiber_g:      decimalField(),
  total_sugars_g:       decimalField(),
  added_sugars_g:       decimalField(),
  protein_g:            decimalField(),

  // -- Vitamins ----------------------------------------------------------------
  vitamin_a_mcg:       decimalField(),
  vitamin_a_iu:        decimalField(),
  vitamin_c_mg:        decimalField(),
  vitamin_d_mcg:       decimalField(),
  vitamin_d_iu:        decimalField(),
  vitamin_e_mg:        decimalField(),
  vitamin_e_iu:        decimalField(),
  vitamin_k_mcg:       decimalField(),
  thiamin_b1_mg:       decimalField(),
  riboflavin_b2_mg:    decimalField(),
  niacin_b3_mg:        decimalField(),
  vitamin_b6_mg:       decimalField(),
  folate_mcg:          decimalField(),
  vitamin_b12_mcg:     decimalField(),
  biotin_mcg:          decimalField(),
  pantothenic_acid_mg: decimalField(),
  choline_mg:          decimalField(),
  inositol_mg:         decimalField(),
  folate_dfe_mcg:      decimalField(),

  // -- Minerals ----------------------------------------------------------------
  calcium_mg:      decimalField(),
  calcium_meq:     decimalField(),
  iron_mg:         decimalField(),
  magnesium_mg:    decimalField(),
  magnesium_meq:   decimalField(),
  phosphorus_mg:   decimalField(),
  phosphorus_mmol: decimalField(),
  potassium_mg:    decimalField(),
  potassium_meq:   decimalField(),
  zinc_mg:         decimalField(),
  copper_mg:       decimalField(),
  manganese_mg:    decimalField(),
  selenium_mcg:    decimalField(),
  chromium_mcg:    decimalField(),
  molybdenum_mcg:  decimalField(),
  iodine_mcg:      decimalField(),
  chloride_mg:     decimalField(),
  chloride_meq:    decimalField(),
  fluoride_mcg:    decimalField(),
  aluminum_mcg:    decimalField(),

  // -- Essential amino acids ---------------------------------------------------
  bcaa_g:          decimalField(),
  isoleucine_g:    decimalField(),
  leucine_g:       decimalField(),
  lysine_g:        decimalField(),
  methionine_g:    decimalField(),
  phenylalanine_g: decimalField(),
  threonine_g:     decimalField(),
  tryptophan_g:    decimalField(),
  valine_g:        decimalField(),
  histidine_g:     decimalField(),

  // -- Non-essential amino acids -----------------------------------------------
  alanine_g:         decimalField(),
  arginine_g:        decimalField(),
  cystine_g:         decimalField(),
  glycine_g:         decimalField(),
  proline_g:         decimalField(),
  serine_g:          decimalField(),
  tyrosine_g:        decimalField(),
  l_aspartic_acid_g: decimalField(),
  l_glutamic_acid_g: decimalField(),
  l_glutamine_g:     decimalField(),

  // -- AA derivatives & additives ----------------------------------------------
  taurine_mg:             decimalField(),
  carnitine_mg:           decimalField(),
  mannitol_mg:            decimalField(),
  lutein_mg:              decimalField(),
  sodium_metabisulfite_g: decimalField(),

  // -- Lipids / Fatty acids ----------------------------------------------------
  total_nitrogen_g:    decimalField(),
  linolenic_omega3_mg: decimalField(),
  ala_g:               decimalField(),
  ara_mg:              decimalField(),
  epa_mg:              decimalField(),
  dha_mg:              decimalField(),
  linoleic_omega6_mg:  decimalField(),
  mct_g:               decimalField(),

  // -- CHO / Fibers ------------------------------------------------------------
  d_galactose_g:     decimalField(),
  dextrose_g:        decimalField(),
  prebiotic_fiber_g: decimalField(),
  soluble_fiber_g:   decimalField(),
  insoluble_fiber_g: decimalField(),

  // -- Packaging ---------------------------------------------------------------
  package_size_display: strField(64),
  unit_of_measure:      strField(32),
  volume_uom:           strField(32),
  net_volume_ml:        decimalField(),
  net_weight_g:         decimalField(),
  container_type:       strField(64),
  units_per_case:       decimalField(),
  storage_conditions:   strField(128),
  shelf_life_months:    decimalField(),

  // -- Barcodes ----------------------------------------------------------------
  primary_unit_barcode:     strField(64),
  primary_unit_2d_barcode:  strField(128),
  carton_barcode:           strField(64),
  primary_units_per_carton: decimalField(),
  primary_units_per_case:   decimalField(),
  case_barcode:             strField(64),

  // -- Storage / Shelf life ----------------------------------------------------
  storage_unopened:                  strField(128),
  storage_opened:                    strField(128),
  shelf_life_after_open_days:        decimalField(),
  shelf_life_after_mix_refrig_hours: decimalField(),
  shelf_life_after_open_unit:        shelfLifeUnit,
  shelf_life_after_mix_refrig_unit:  shelfLifeUnit,

  // -- Energy / Density / Prep -------------------------------------------------
  kcal_per_g:             decimalField(),
  kcal_per_ml:            decimalField(),
  displacement_ml_per_g:  decimalField(),
  water_g:                decimalField(),
  prsl_mosm_per_100kcal:  decimalField(),
  osmolality_mosm_per_kg: decimalField(),
  osmolarity_mosm_per_l:  decimalField(),
  scoop_g:                decimalField(),
  teaspoon_g:             decimalField(),
  cup_g:                  decimalField(),
  nutrient_basis:          strField(32),
  nutrient_basis_is_volume: boolField(),
  ml_per_100kcal:          decimalField(),
  recipe_dilution:         strField(128),
  denominator_property:   strField(64),
  denominator_volume:     decimalField(),

  // -- Classification ----------------------------------------------------------
  age_for_use:        strField(64),
  protein_source:     strField(128),
  protein_hydrolysis: strField(64),
  cho_source:         strField(128),
  region_for_use:     strField(32),
  flavor:             strField(64),
  measurement_metric: strField(64),

  // -- Clinical metrics --------------------------------------------------------
  renal_solute_load_mosml: decimalField(),
  acetate_meq:             decimalField(),
  ingredients:             z.string().nullable().optional(),

  // -- Dietary certifications --------------------------------------------------
  kosher:             boolField(),
  halal:              boolField(),
  gluten_free:        boolField(),
  regulatory_notes:   z.string().nullable().optional(),
  warnings:           z.string().nullable().optional(),
  manufacturer_notes: z.string().nullable().optional(),
  extras:             z.record(z.string(), z.unknown()).nullable().optional(),

  // -- Timestamps --------------------------------------------------------------
  attributes_updated_at: z.string().nullable().optional(),
} as const;

// ---------------------------------------------------------------------------
// Product List Item -- core identity fields for list views
// ---------------------------------------------------------------------------
export const ProductListItemSchema = z.object({
  product_id:        uuidSchema,
  sku:               z.string().max(64),
  product_name:      z.string().max(255).nullable().optional(),
  tenant_id:         z.string().nullable().optional(),
  display_name:      strField(255),
  image_url:         z.string().url().nullable().optional(),
  brand_name:        strField(255),
  form_name:         strField(64),
  category_name:     strArrayField(128),
  manufacturer_name: strField(255),
  description:       z.string().nullable().optional(),
  type_of_product:   strField(64),
  item_type:         strField(64),
  status:            z.enum(PRODUCT_STATUSES).nullable().optional(),
  is_active:         boolField(),
  created_at:        z.string(),
  updated_at:        z.string(),
});

export type ProductListItem = z.infer<typeof ProductListItemSchema>;

// ---------------------------------------------------------------------------
// Product Detail -- full flat schema (all SQL columns)
// ---------------------------------------------------------------------------
export const ProductDetailSchema = ProductListItemSchema.extend({
  ...productFlatFields,
  // Override z.preprocess fields so ZodTypeProvider infers the correct
  // `string | null | undefined` output type instead of `unknown`.
  master_product_id: uuidSchema.nullable().optional(),
  catalog_added_by:  uuidSchema.nullable().optional(),
});

export type ProductDetail = z.infer<typeof ProductDetailSchema>;

// ---------------------------------------------------------------------------
// Response schemas (coerce Decimal / Date from DynamoDB raw values)
// ---------------------------------------------------------------------------
export const ProductListItemResponseSchema = ProductListItemSchema.extend({
  created_at: z.coerce.date().transform((d) => d.toISOString()),
  updated_at: z.coerce.date().transform((d) => d.toISOString()),
});

export const ProductDetailResponseSchema = ProductDetailSchema.extend({
  image_url:     z.string().nullable().optional(),
  created_at:    z.coerce.date().transform((d) => d.toISOString()),
  updated_at:    z.coerce.date().transform((d) => d.toISOString()),
  date_verified: z.coerce.date().transform((d) => d.toISOString()).nullable().optional(),
});

// Backward-compat aliases so other files that import these names still compile
/** @deprecated Use ProductDetailSchema directly */
export const ProductAttributesSchema = z.object(productFlatFields);
export type ProductAttributes = z.infer<typeof ProductAttributesSchema>;
/** @deprecated Use ProductDetailResponseSchema instead */
export const ProductAttributesResponseSchema = ProductAttributesSchema;

// ---------------------------------------------------------------------------
// Create request -- all flat fields optional except sku + brand_name
// ---------------------------------------------------------------------------
export const CreateProductRequestSchema = z.object({
  // -- Required ---------------------------------------------------------------
  sku:          z.string().min(1).max(64),
  brand_name:   z.string().min(1).max(255),
  product_name: z.string().min(1).max(255).optional(),

  // -- Identity ---------------------------------------------------------------
  tenant_id:         z.string().nullable().optional(),
  display_name:      strField(255),
  image_url:         z.preprocess((v) => (v === '' ? null : v), z.string().url().nullable().optional()),
  form_name:         z.preprocess(
    (v) => normalizeFormType(typeof v === 'string' ? v.trim() : v),
    z.string().min(1).max(64),
  ),
  category_name:     z.array(z.string().trim().min(1).max(128)).min(0),
  manufacturer_name: z.string().min(1).max(255),
  description:       z.string().nullable().optional(),
  type_of_product:   strField(64),
  item_type:         strField(64),
  /** Defaults to draft. Set publish: true to create as active. */
  status:           z.enum(PRODUCT_STATUSES).nullable().optional(),
  is_active:         z.boolean().default(true),

  // -- Coding & Formulary -----------------------------------------------------
  tms_product_id:          strField(64),
  hcpcs:                   strField(32),
  ndc_format_code:         strField(32),
  overarching_number_pens: strField(64),
  overarching_number_ns:   strField(64),
  overarching_number_tmnp: strField(64),

  // -- Batch / Verification ---------------------------------------------------
  csv_batch_ref:       strField(128),
  date_verified:       z.string().nullable().optional(),
  verification_source: z.string().nullable().optional(),
  substitutions:       z.string().nullable().optional(),
  similar_products:    z.array(z.string()).nullable().optional(),

  // -- Allergen flags ----------------------------------------------------------
  contains_allergen_milk:       boolField(),
  contains_allergen_egg:        boolField(),
  contains_allergen_wheat:      boolField(),
  contains_allergen_soy:        boolField(),
  contains_allergen_peanut:     boolField(),
  contains_allergen_tree_nuts:  boolField(),
  contains_allergen_fish:       boolField(),
  contains_allergen_shellfish:  boolField(),
  contains_allergen_sesame:     boolField(),
  contains_allergen_celery:     boolField(),
  contains_allergen_mustard:    boolField(),
  contains_allergen_lupin:      boolField(),
  contains_allergen_molluscs:   boolField(),
  contains_allergen_sulphites:  boolField(),
  contains_allergen_lactose:    boolField(),
  contains_allergen_latex:      boolField(),
  contains_allergen_gluten:     boolField(),

  // -- Macronutrients ----------------------------------------------------------
  calories_kcal:        decimalField(),
  total_fat_g:          decimalField(),
  saturated_fat_g:      decimalField(),
  trans_fat_g:          decimalField(),
  cholesterol_mg:       decimalField(),
  cho_g:                decimalField(),
  sodium_mg:            decimalField(),
  sodium_meq:           decimalField(),
  total_carbohydrate_g: decimalField(),
  dietary_fiber_g:      decimalField(),
  total_sugars_g:       decimalField(),
  added_sugars_g:       decimalField(),
  protein_g:            decimalField(),

  // -- Vitamins ----------------------------------------------------------------
  vitamin_a_mcg:       decimalField(),
  vitamin_c_mg:        decimalField(),
  vitamin_d_mcg:       decimalField(),
  vitamin_e_mg:        decimalField(),
  vitamin_k_mcg:       decimalField(),
  thiamin_b1_mg:       decimalField(),
  riboflavin_b2_mg:    decimalField(),
  niacin_b3_mg:        decimalField(),
  vitamin_b6_mg:       decimalField(),
  folate_mcg:          decimalField(),
  vitamin_b12_mcg:     decimalField(),
  biotin_mcg:          decimalField(),
  pantothenic_acid_mg: decimalField(),
  choline_mg:          decimalField(),
  inositol_mg:         decimalField(),
  folate_dfe_mcg:      decimalField(),

  // -- Minerals ----------------------------------------------------------------
  calcium_mg:      decimalField(),
  calcium_meq:     decimalField(),
  iron_mg:         decimalField(),
  magnesium_mg:    decimalField(),
  magnesium_meq:   decimalField(),
  phosphorus_mg:   decimalField(),
  phosphorus_mmol: decimalField(),
  potassium_mg:    decimalField(),
  potassium_meq:   decimalField(),
  zinc_mg:         decimalField(),
  copper_mg:       decimalField(),
  manganese_mg:    decimalField(),
  selenium_mcg:    decimalField(),
  chromium_mcg:    decimalField(),
  molybdenum_mcg:  decimalField(),
  iodine_mcg:      decimalField(),
  chloride_mg:     decimalField(),
  chloride_meq:    decimalField(),
  fluoride_mcg:    decimalField(),
  aluminum_mcg:    decimalField(),

  // -- Amino acids -------------------------------------------------------------
  isoleucine_g:      decimalField(),
  leucine_g:         decimalField(),
  lysine_g:          decimalField(),
  methionine_g:      decimalField(),
  phenylalanine_g:   decimalField(),
  threonine_g:       decimalField(),
  tryptophan_g:      decimalField(),
  valine_g:          decimalField(),
  histidine_g:       decimalField(),
  alanine_g:         decimalField(),
  arginine_g:        decimalField(),
  cystine_g:         decimalField(),
  glycine_g:         decimalField(),
  proline_g:         decimalField(),
  serine_g:          decimalField(),
  tyrosine_g:        decimalField(),
  l_aspartic_acid_g: decimalField(),
  l_glutamic_acid_g: decimalField(),
  l_glutamine_g:     decimalField(),
  taurine_mg:        decimalField(),
  carnitine_mg:      decimalField(),

  // -- Fatty acids -------------------------------------------------------------
  total_nitrogen_g:    decimalField(),
  linolenic_omega3_mg: decimalField(),
  ala_g:               decimalField(),
  ara_mg:              decimalField(),
  epa_mg:              decimalField(),
  dha_mg:              decimalField(),
  linoleic_omega6_mg:  decimalField(),
  mct_g:               decimalField(),

  // -- Other nutrients ---------------------------------------------------------
  mannitol_mg:            decimalField(),
  lutein_mg:              decimalField(),
  sodium_metabisulfite_g: decimalField(),
  d_galactose_g:          decimalField(),
  dextrose_g:             decimalField(),
  prebiotic_fiber_g:      decimalField(),
  soluble_fiber_g:        decimalField(),
  insoluble_fiber_g:      decimalField(),

  // -- Energy / Physical -------------------------------------------------------
  kcal_per_g:              decimalField(),
  kcal_per_ml:             decimalField(),
  displacement_ml_per_g:   decimalField(),
  water_g:                 decimalField(),
  prsl_mosm_per_100kcal:   decimalField(),
  osmolality_mosm_per_kg:  decimalField(),
  osmolarity_mosm_per_l:   decimalField(),
  renal_solute_load_mosml: decimalField(),
  acetate_meq:             decimalField(),
  ml_per_100kcal:          decimalField(),

  // -- Serving / Prep ----------------------------------------------------------
  scoop_g:                  decimalField(),
  teaspoon_g:               decimalField(),
  cup_g:                    decimalField(),
  nutrient_basis:           strField(32),
  nutrient_basis_is_volume: boolField(),
  recipe_dilution:          strField(128),

  // -- Packaging ---------------------------------------------------------------
  package_size_display:     strField(64),
  unit_of_measure:          strField(32),
  volume_uom:               strField(32),
  net_volume_ml:            decimalField(),
  net_weight_g:             decimalField(),
  container_type:           strField(64),
  units_per_case:           decimalField(),
  primary_unit_barcode:     strField(64),
  primary_unit_2d_barcode:  strField(128),
  carton_barcode:           strField(64),
  primary_units_per_carton: decimalField(),
  primary_units_per_case:   decimalField(),
  case_barcode:             strField(64),

  // -- Storage / Shelf life ----------------------------------------------------
  storage_conditions:                strField(128),
  storage_unopened:                  strField(128),
  storage_opened:                    strField(128),
  shelf_life_months:                 decimalField(),
  shelf_life_after_open_days:        decimalField(),
  shelf_life_after_mix_refrig_hours: decimalField(),
  shelf_life_after_open_unit:        shelfLifeUnit,
  shelf_life_after_mix_refrig_unit:  shelfLifeUnit,

  // -- Classification ----------------------------------------------------------
  age_for_use:        strField(64),
  protein_source:     strField(128),
  protein_hydrolysis: strField(64),
  cho_source:         strField(128),
  region_for_use:     strField(32),
  flavor:             strField(64),
  measurement_metric: strField(64),

  // -- Composition / Dietary ---------------------------------------------------
  ingredients:        z.string().nullable().optional(),
  kosher:             boolField(),
  halal:              boolField(),
  gluten_free:        boolField(),
  regulatory_notes:   z.string().nullable().optional(),
  warnings:           z.string().nullable().optional(),
  manufacturer_notes: z.string().nullable().optional(),
  extras:             z.record(z.string(), z.unknown()).nullable().optional(),
});

export type CreateProductRequest = z.infer<typeof CreateProductRequestSchema>;

// ---------------------------------------------------------------------------
// Patch request -- same as create but every field optional + status allowed
// ---------------------------------------------------------------------------
export const PatchProductRequestSchema = CreateProductRequestSchema
  //.omit({ is_active: true })
  .extend({
    sku:          z.string().min(1).max(64).optional(),
    product_name: z.string().min(1).max(255).optional(),
    status:       z.enum(PRODUCT_STATUSES).optional(),
    needs_review: boolField(),
  })
  .partial()
  .required({
    sku:               true,
    product_name:      true,
    brand_name:        true,
    manufacturer_name: true,
    category_name:     true,
    form_name:         true,
  });

export type PatchProductRequest = z.infer<typeof PatchProductRequestSchema>;

// ---------------------------------------------------------------------------
// GET /products -- query params: search, filter, sort, pagination
// ---------------------------------------------------------------------------
export const PRODUCT_SORT_FIELDS = [
  'product_name',
  'sku',
  'status',
  'created_at',
  'updated_at',
] as const;

export type ProductSortField = (typeof PRODUCT_SORT_FIELDS)[number];

export const ProductListQuerySchema = z.object({
  search:            z.string().max(200).optional(),
  tenant_id:         z.string().nullable().optional(),
  status:            z.enum(PRODUCT_STATUSES).optional(),
  item_type:         z.string().max(64).optional(),
  brand_name:        z.string().max(255).optional(),
  category_name:     z.array(z.string().max(128)).optional(),
  manufacturer_name: z.string().max(255).optional(),
  form_name:         z.string().max(64).optional(),
  region_for_use:    z.string().max(32).optional(),
  needs_review:      z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
  is_active:         z.enum(['true', 'false']).transform((v) => v === 'true').default(true),
  sort_by:           z.enum(PRODUCT_SORT_FIELDS).default('created_at'),
  sort_dir:          z.enum(['asc', 'desc']).default('desc'),
  page:              z.coerce.number().int().min(1).default(1),
  limit:             z.coerce.number().int().min(1).max(100).default(20),
});

export type ProductListQuery = z.infer<typeof ProductListQuerySchema>;

// ---------------------------------------------------------------------------
// CSV Upload -- row schema & result types
// ---------------------------------------------------------------------------

// Empty-string helpers for CSV cells:
//   csvOptNum  — '' or null → null; otherwise coerce to number
//   csvOptBool — '' or null → undefined; 'true'/true → true; anything else → false
const csvOptNum = z.preprocess(
  (v) => (v === '' || v === null ? null : v),
  z.coerce.number().nullable().optional(),
);
const csvOptBool = z.preprocess(
  (v) => (v === '' || v == null ? undefined : v === 'true' || v === true),
  z.boolean().optional(),
);

// Strict boolean for is_active: lowercases the raw string, then only accepts
// 'true' or 'false'. Any other non-empty value fails validation, which causes
// the CSV row to be skipped and reported as an error.
const csvIsActiveBool = z
  .preprocess(
    (v) => (v === '' || v == null ? undefined : v),
    z.string().optional().transform((v) => (v === undefined ? undefined : v.toLowerCase())),
  )
  .pipe(
    z
      .enum(['true', 'false'])
      .optional()
      .transform((v) => (v === undefined ? undefined : v === 'true')),
  );

export const CsvProductRowSchema = z.object({
  // ── Required ──────────────────────────────────────────────────────────────
  sku:          z.string().min(1, 'SKU is required').max(64),
  product_name: z.string().max(255).optional().nullable(),

  // ── Identity ──────────────────────────────────────────────────────────────
  tenant_id:         z.string().nullable().optional(),
  display_name:      z.string().max(255).optional(),
  // empty string → treated as absent ('' would fail url() otherwise)
  image_url:         z.preprocess((v) => (v === '' ? undefined : v), z.string().url().optional()),
  brand_name:        z.string().min(1, 'brand_name is required').max(255),
  form_name:         z.preprocess(
    (v) => (v === '' ? undefined : normalizeFormType(v)),
    z.enum(PRODUCT_FORM_TYPES, {
      error: `form_name must be one of: ${PRODUCT_FORM_TYPES.join(', ')}`,
    }),
  ),
  // pipe-separated string from CSV (e.g. "Enteral|Oral") or empty → undefined
  category_name:     z.preprocess(
    (v) => (v === '' || v == null ? undefined : typeof v === 'string' ? v.split('|').map((s) => s.trim()).filter(Boolean) : v),
    z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  ),
  manufacturer_name: z.string().min(1, 'manufacturer_name is required').max(255),
  description:       z.string().optional(),
  type_of_product:   z.string().max(64).optional(),
  item_type:         z.string().max(64).optional(),
  // empty string → treated as absent ('' is not a valid enum member); defaults to 'active'
  status:            z.preprocess((v) => (v === '' ? undefined : v), z.enum(PRODUCT_STATUSES).optional().default('active')),
  is_active:         csvIsActiveBool,

  // ── Coding & Formulary ────────────────────────────────────────────────────
  tms_product_id:          z.string().max(64).optional(),
  hcpcs:                   z.string().max(32).optional(),
  ndc_format_code:         z.string().max(32).optional(),
  overarching_number_pens: z.string().max(64).optional(),
  overarching_number_ns:   z.string().max(64).optional(),
  overarching_number_tmnp: z.string().max(64).optional(),

  // ── Verification ─────────────────────────────────────────────────────────
  date_verified:       z.string().optional(),
  verification_source: z.string().optional(),
  substitutions:       z.string().optional(),

  // ── Allergen flags ────────────────────────────────────────────────────────
  contains_allergen_milk:      csvOptBool,
  contains_allergen_egg:       csvOptBool,
  contains_allergen_wheat:     csvOptBool,
  contains_allergen_soy:       csvOptBool,
  contains_allergen_peanut:    csvOptBool,
  contains_allergen_tree_nuts: csvOptBool,
  contains_allergen_fish:      csvOptBool,
  contains_allergen_shellfish: csvOptBool,
  contains_allergen_sesame:    csvOptBool,
  contains_allergen_celery:    csvOptBool,
  contains_allergen_mustard:   csvOptBool,
  contains_allergen_lupin:     csvOptBool,
  contains_allergen_molluscs:  csvOptBool,
  contains_allergen_sulphites: csvOptBool,
  contains_allergen_lactose:   csvOptBool,
  contains_allergen_latex:     csvOptBool,
  contains_allergen_gluten:    csvOptBool,

  // ── Macronutrients ────────────────────────────────────────────────────────
  calories_kcal:        z.preprocess(
    (v) => (v === '' || v === null ? undefined : v),
    z.coerce.number({ error: 'calories_kcal is required and must be a number' }),
  ),
  total_fat_g:          csvOptNum,
  saturated_fat_g:      csvOptNum,
  trans_fat_g:          csvOptNum,
  cholesterol_mg:       csvOptNum,
  cho_g:                csvOptNum,
  sodium_mg:            csvOptNum,
  sodium_meq:           csvOptNum,
  total_carbohydrate_g: csvOptNum,
  dietary_fiber_g:      csvOptNum,
  total_sugars_g:       csvOptNum,
  added_sugars_g:       csvOptNum,
  protein_g:            csvOptNum,

  // ── Vitamins ──────────────────────────────────────────────────────────────
  vitamin_a_mcg:       csvOptNum,
  vitamin_a_iu:        csvOptNum,
  vitamin_c_mg:        csvOptNum,
  vitamin_d_mcg:       csvOptNum,
  vitamin_d_iu:        csvOptNum,
  vitamin_e_mg:        csvOptNum,
  vitamin_e_iu:        csvOptNum,
  vitamin_k_mcg:       csvOptNum,
  thiamin_b1_mg:       csvOptNum,
  thiamin_b1_mcg:      csvOptNum,
  riboflavin_b2_mg:    csvOptNum,
  riboflavin_b2_mcg:   csvOptNum,
  niacin_b3_mg:        csvOptNum,
  niacin_b3_mcg:       csvOptNum,
  vitamin_b6_mg:       csvOptNum,
  vitamin_b6_mcg:      csvOptNum,
  folate_mcg:          csvOptNum,
  vitamin_b12_mcg:     csvOptNum,
  biotin_mcg:          csvOptNum,
  pantothenic_acid_mg: csvOptNum,
  pantothenic_acid_mcg: csvOptNum,
  choline_mg:          csvOptNum,
  inositol_mg:         csvOptNum,
  folate_dfe_mcg:      csvOptNum,

  // ── Minerals ──────────────────────────────────────────────────────────────
  calcium_mg:      csvOptNum,
  calcium_meq:     csvOptNum,
  iron_mg:         csvOptNum,
  magnesium_mg:    csvOptNum,
  magnesium_meq:   csvOptNum,
  phosphorus_mg:   csvOptNum,
  phosphorus_mmol: csvOptNum,
  potassium_mg:    csvOptNum,
  potassium_meq:   csvOptNum,
  zinc_mg:         csvOptNum,
  copper_mg:       csvOptNum,
  copper_mcg:      csvOptNum,
  manganese_mg:    csvOptNum,
  manganese_mcg:   csvOptNum,
  selenium_mcg:    csvOptNum,
  chromium_mcg:    csvOptNum,
  molybdenum_mcg:  csvOptNum,
  iodine_mcg:      csvOptNum,
  chloride_mg:     csvOptNum,
  chloride_meq:    csvOptNum,
  fluoride_mcg:    csvOptNum,
  aluminum_mcg:    csvOptNum,

  // ── Amino acids ───────────────────────────────────────────────────────────
  bcaa_g:                 csvOptNum,
  isoleucine_g:           csvOptNum,
  leucine_g:              csvOptNum,
  lysine_g:               csvOptNum,
  methionine_g:           csvOptNum,
  phenylalanine_g:        csvOptNum,
  threonine_g:            csvOptNum,
  tryptophan_g:           csvOptNum,
  valine_g:               csvOptNum,
  histidine_g:            csvOptNum,
  alanine_g:              csvOptNum,
  arginine_g:             csvOptNum,
  cystine_g:              csvOptNum,
  glycine_g:              csvOptNum,
  proline_g:              csvOptNum,
  serine_g:               csvOptNum,
  tyrosine_g:             csvOptNum,
  l_aspartic_acid_g:      csvOptNum,
  l_glutamic_acid_g:      csvOptNum,
  l_glutamine_g:          csvOptNum,
  taurine_mg:             csvOptNum,
  carnitine_mg:           csvOptNum,
  mannitol_mg:            csvOptNum,
  lutein_mg:              csvOptNum,
  sodium_metabisulfite_g: csvOptNum,

  // ── Fatty acids ───────────────────────────────────────────────────────────
  total_nitrogen_g:    csvOptNum,
  linolenic_omega3_mg: csvOptNum,
  ala_g:               csvOptNum,
  ara_mg:              csvOptNum,
  epa_mg:              csvOptNum,
  dha_mg:              csvOptNum,
  linoleic_omega6_mg:  csvOptNum,
  mct_g:               csvOptNum,

  // ── CHO / Fibers ──────────────────────────────────────────────────────────
  d_galactose_g:     csvOptNum,
  dextrose_g:        csvOptNum,
  prebiotic_fiber_g: csvOptNum,
  soluble_fiber_g:   csvOptNum,
  insoluble_fiber_g: csvOptNum,

  // ── Energy / Physical ─────────────────────────────────────────────────────
  kcal_per_g:              csvOptNum,
  kcal_per_ml:             csvOptNum,
  displacement_ml_per_g:   csvOptNum,
  water_g:                 csvOptNum,
  prsl_mosm_per_100kcal:   csvOptNum,
  osmolality_mosm_per_kg:  csvOptNum,
  osmolarity_mosm_per_l:   csvOptNum,
  renal_solute_load_mosml: csvOptNum,
  acetate_meq:             csvOptNum,
  ml_per_100kcal:          csvOptNum,

  // ── Serving / Prep ────────────────────────────────────────────────────────
  scoop_g:                  csvOptNum,
  teaspoon_g:               csvOptNum,
  cup_g:                    csvOptNum,
  nutrient_basis:           z.string().max(32).optional(),
  nutrient_basis_is_volume: csvOptBool,
  recipe_dilution:          z.string().max(128).optional(),

  // ── Packaging ────────────────────────────────────────────────────────────
  package_size_display:     z.string().min(1, 'package_size_display is required').max(64),
  unit_of_measure:          z.string().max(32).optional(),
  volume_uom:               z.string().min(1).max(32),
  net_volume_ml:            csvOptNum,
  net_weight_g:             csvOptNum,
  container_type:           z.string().max(64).optional(),
  units_per_case:           csvOptNum,
  primary_unit_barcode:     z.string().max(64).optional(),
  primary_unit_2d_barcode:  z.string().max(128).optional(),
  carton_barcode:           z.string().max(64).optional(),
  primary_units_per_carton: csvOptNum,
  primary_units_per_case:   csvOptNum,
  case_barcode:             z.string().max(64).optional(),

  // ── Storage / Shelf life ──────────────────────────────────────────────────
  storage_conditions:                z.string().max(128).optional(),
  storage_unopened:                  z.string().max(128).optional(),
  storage_opened:                    z.string().max(128).optional(),
  shelf_life_months:                 csvOptNum,
  shelf_life_after_open_days:        csvOptNum,
  shelf_life_after_mix_refrig_hours: csvOptNum,
  shelf_life_after_open_unit:        shelfLifeUnit,
  shelf_life_after_mix_refrig_unit:  shelfLifeUnit,

  // ── Classification ────────────────────────────────────────────────────────
  age_for_use:            z.string().max(64).optional(),
  protein_source:         z.string().max(128).optional(),
  protein_hydrolysis:     z.string().max(64).optional(),
  cho_source:             z.string().max(128).optional(),
  region_for_use:         z.string().max(32).optional(),
  flavor:                 z.string().max(64).optional(),
  measurement_metric:     z.string().max(64).optional(),
  formula_classification: z.string().max(128).optional(),
  parenteral_category:    z.string().max(128).optional(),

  // ── Composition / Dietary ─────────────────────────────────────────────────
  ingredients:        z.string().optional(),
  kosher:             csvOptBool,
  halal:              csvOptBool,
  gluten_free:        csvOptBool,
  regulatory_notes:   z.string().optional(),
  warnings:           z.string().optional(),
  manufacturer_notes: z.string().optional(),

  // ── Review / Similarity ─────────────────────────────────────────────────
  needs_review:       csvOptBool,
  similar_products:   z.preprocess(
    (v) => {
      if (typeof v === 'string') {
        const trimmed = v.trim();
        if (!trimmed) return undefined;
        return trimmed.split(',').map((s) => s.trim()).filter(Boolean);
      }
      return v == null ? undefined : v;
    },
    z.array(z.string()).optional(),
  ),
  similarity_details: z.string().optional(),
});

export type CsvProductRow = z.infer<typeof CsvProductRowSchema>;

export const CsvRowErrorSchema = z.object({
  row:     z.number().int(),
  sku:     z.string().optional(),
  field:   z.string(),
  message: z.string(),
  value:   z.string().optional(),
});

export type CsvRowError = z.infer<typeof CsvRowErrorSchema>;

export const CsvUploadResultSchema = z.object({
  batchId:        z.string(),
  auditLogId:     z.string(),
  totalRows:      z.number().int(),
  validRows:      z.number().int(),
  skippedRows:    z.number().int(),
  insertedRows:   z.number().int(),
  updatedRows:    z.number().int(),
  unchangedRows:  z.number().int(),
  errors:         z.array(CsvRowErrorSchema),
});

export type CsvUploadResult = z.infer<typeof CsvUploadResultSchema>;
